import { PLATFORMS } from "../../constants/constants";
import {
  fetchAsanaWorkspaces,
  fetchAsanaProjects,
  fetchTrelloWorkspaces,
  fetchTrelloBoards,
  fetchTodoistWorkspaces,
  fetchTodoistProjects,
  fetchGithubOrganizations,
  fetchGithubRepositories,
  fetchJiraWorkspaces,
  fetchJiraProjects,
  fetchLinearWorkspaces,
  fetchLinearProjects,
  fetchClickupWorkspaces,
  fetchClickupProjects,
  fetchJiraBoards,
  fetchClickupLists,
  fetchPipedriveOrganizations,
} from "../../features/oauth/oauthAPI";

export const PLATFORM_OPTIONS = {
  [PLATFORMS.SLACK?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [],
    title: "Select Default Settings",
  },
  [PLATFORMS.GMAIL?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [],
    title: "Select Default Settings",
  },
  [PLATFORMS.OUTLOOK?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [],
    title: "Select Default Settings",
  },
  [PLATFORMS.GOOGLE_CALENDAR?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [],
    title: "Select Default Settings",
  },
  [PLATFORMS.OUTLOOK_CALENDAR?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [],
    title: "Select Default Settings",
  },
  [PLATFORMS.MICROSOFT_TODO?.toLowerCase()]: {
    settingsLoad: "onLoad",
    preSteps: [],
    title: "Select Default Settings",
  },
  [PLATFORMS.ASANA?.toLowerCase()]: {
    settingsLoad: "onItemSelect",
    preSteps: [
      { key: "workspace", label: "Select Workspace", dataKey: "workspaces" },
      { key: "item", label: "Select Project", dataKey: "items" },
    ],
    title: "Select Asana Workspace & Project",
  },
  [PLATFORMS.TRELLO?.toLowerCase()]: {
    settingsLoad: "onItemSelect",
    preSteps: [
      { key: "workspace", label: "Select Workspace", dataKey: "workspaces" },
      { key: "item", label: "Select Board", dataKey: "items" },
    ],
    title: "Select Trello Workspace & Board",
  },
  [PLATFORMS.TODOIST?.toLowerCase()]: {
    settingsLoad: "onItemSelect",
    fallbackToItems: true,
    preSteps: [
      { key: "workspace", label: "Select Workspace", dataKey: "workspaces" },
      { key: "item", label: "Select Project", dataKey: "items" },
    ],
    title: "Select Todoist Project",
  },
  [PLATFORMS.GITHUB?.toLowerCase()]: {
    settingsLoad: "onItemSelect",
    preSteps: [
      { key: "workspace", label: "Select Organization", dataKey: "workspaces" },
      { key: "item", label: "Select Repository", dataKey: "items" },
    ],
    title: "Select GitHub Organization & Repository",
  },
  [PLATFORMS.JIRA?.toLowerCase()]: {
    settingsLoad: "onItemSelect",
    autoAdvance: false,
    preSteps: [
      { key: "workspace", label: "Workspace", dataKey: "workspaces" },
      { key: "project", label: "Project", dataKey: "projects" },
      { key: "board", label: "Board", dataKey: "items" },
    ],
    title: "Select Jira Workspace, Project & Board",
  },
  [PLATFORMS.NOTION?.toLowerCase()]: {
    settingsLoad: "onItemSelect",
    isDatabaseOnly: true,
    preSteps: [{ key: "database", label: "Select Database", dataKey: "items" }],
    title: "Select Notion Database",
  },
  [PLATFORMS.LINEAR?.toLowerCase()]: {
    settingsLoad: "onItemSelect",
    preSteps: [
      { key: "workspace", label: "Select Workspace", dataKey: "workspaces" },
      { key: "item", label: "Select Team", dataKey: "projects" },
    ],
    title: "Select Linear Workspace & Team",
  },
  [PLATFORMS.CLICKUP?.toLowerCase()]: {
    settingsLoad: "onItemSelect",
    autoAdvance: false,
    preSteps: [
      {
        key: "workspace",
        label: "Select Workspace",
        dataKey: "workspaces",
      },
      {
        key: "project",
        label: "Select Space",
        dataKey: "projects",
      },
      {
        key: "item",
        label: "Select List",
        dataKey: "items",
      },
    ],
    title: "Select ClickUp Workspace, Space & List",
  },
  [PLATFORMS.PIPEDRIVE?.toLowerCase()]: {
    settingsLoad: "onItemSelect",
    itemsOnLoad: true,
    preSteps: [{ key: "item", label: "Select Organization", dataKey: "items" }],
    title: "Select Pipedrive Organization",
  },
};

export const PLATFORM_SETTINGS_ON_LOAD = Object.entries(PLATFORM_OPTIONS)
  .filter(([, cfg]) => cfg.settingsLoad === "onLoad")
  .map(([key]) => key);

export const PLATFORM_SETTINGS_ON_PROJECT_SELECT = Object.entries(
  PLATFORM_OPTIONS,
)
  .filter(([, cfg]) => cfg.settingsLoad !== "onLoad")
  .map(([key]) => key);

export const WORKSPACE_PLATFORMS = Object.entries(PLATFORM_OPTIONS)
  .filter(([, cfg]) => cfg.preSteps.some((s) => s.key === "workspace"))
  .map(([key]) => key);

export const DATABASE_ONLY_PLATFORMS = Object.entries(PLATFORM_OPTIONS)
  .filter(([, cfg]) => cfg.isDatabaseOnly)
  .map(([key]) => key);

export const WORKSPACE_FETCHERS = {
  [PLATFORMS.ASANA?.toLowerCase()]: (uuid, assistantId) =>
    fetchAsanaWorkspaces(uuid, assistantId),
  [PLATFORMS.TRELLO?.toLowerCase()]: (uuid) => fetchTrelloWorkspaces(uuid),
  [PLATFORMS.TODOIST?.toLowerCase()]: (uuid) => fetchTodoistWorkspaces(uuid),
  [PLATFORMS.GITHUB?.toLowerCase()]: (uuid) => fetchGithubOrganizations(uuid),
  [PLATFORMS.JIRA?.toLowerCase()]: (uuid) => fetchJiraWorkspaces(uuid),
  [PLATFORMS.LINEAR?.toLowerCase()]: (uuid) => fetchLinearWorkspaces(uuid),
  [PLATFORMS.CLICKUP?.toLowerCase()]: (uuid) => fetchClickupWorkspaces(uuid),
};

export const ITEMS_FETCHERS = {
  [PLATFORMS.ASANA?.toLowerCase()]: (uuid, workspaceId) =>
    fetchAsanaProjects(uuid, workspaceId),
  [PLATFORMS.TRELLO?.toLowerCase()]: (uuid, workspaceId) =>
    fetchTrelloBoards(uuid, workspaceId),
  [PLATFORMS.TODOIST?.toLowerCase()]: (uuid, workspaceId) =>
    fetchTodoistProjects(uuid, workspaceId),
  [PLATFORMS.GITHUB?.toLowerCase()]: (uuid, workspaceId) =>
    fetchGithubRepositories(uuid, workspaceId),
  [PLATFORMS.JIRA?.toLowerCase()]: (uuid, workspaceId) =>
    fetchJiraProjects(uuid, workspaceId),
  [PLATFORMS.LINEAR?.toLowerCase()]: (uuid, workspaceId) =>
    fetchLinearProjects(uuid, workspaceId),
  [PLATFORMS.CLICKUP?.toLowerCase()]: (uuid, workspaceId) =>
    fetchClickupProjects(uuid, workspaceId),
  [PLATFORMS.PIPEDRIVE?.toLowerCase()]: (uuid) =>
    fetchPipedriveOrganizations(uuid),
};

export const normalize = (value) =>
  String(value || "")
    .replace(/\s+/g, "")
    .trim();

export const NON_ARRAY_FIELDS = new Set([
  "due_on",
  "calendarId",
  "list",
  "assignee",
  "lists",
  "permission",
  "project",
  "milestone",
  "board",
  "sprint",
  "priority",
  "statuses",
]);

export const DEFAULT_SETTINGS_STEP = {
  key: "defaultSettings",
  label: "Default Settings",
};

export const STRIP_SELECT_RE = /^Select\s+/i;

export const buildSettingPayload = ({
  setting,
  selectedWorkspace,
  selectedProject,
  selectedItem,
  workspaces = [],
  projects = [],
  items = [],
  selectedDefaultSettings = {},
  isUpdateMode = false,
  mergeDefaultSettings,
  settingId,
  platformName,
}) => {
  let integrationIds = setting?.integrationIds || {};

  if (!isUpdateMode) {
    const matchedWorkspace =
      workspaces.find(
        (w) =>
          String(w.id) === String(selectedWorkspace) ||
          String(w.gid) === String(selectedWorkspace) ||
          String(w.legacyId) === String(selectedWorkspace),
      ) || null;

    const matchedProject =
      projects.find(
        (p) =>
          String(p.id) === String(selectedProject) ||
          String(p.gid) === String(selectedProject) ||
          String(p.legacyId) === String(selectedProject),
      ) || null;

    const matchedItem =
      items.find(
        (i) =>
          String(i.id) === String(selectedItem) ||
          String(i.gid) === String(selectedItem) ||
          String(i.legacyId) === String(selectedItem),
      ) || null;
      
    const isPipedrive =
      platformName?.toLowerCase() === PLATFORMS.PIPEDRIVE?.toLowerCase();

    if (isPipedrive) {
      integrationIds = {
        ...integrationIds,
        id1: selectedItem ? String(selectedItem) : "",
        id2: "",
        id3: "",
        id1_metadata: matchedItem,
        id2_metadata: null,
      };
    } else if (isThreeStepPlatform(platformName)) {
      integrationIds = {
        ...integrationIds,
        id1: matchedWorkspace?.id || "",
        id2: matchedProject?.id || "",
        id3: matchedItem?.id || "",
        id1_metadata: matchedWorkspace || null,
        id2_metadata: matchedProject || null,
        id3_metadata: matchedItem || null,
      };
    } else {
      integrationIds = {
        ...integrationIds,
        id1: selectedWorkspace,
        id2: selectedItem,
        id3: matchedItem?.boardId || integrationIds?.id3 || "",
        id1_metadata: matchedWorkspace,
        id2_metadata: matchedItem,
      };
    }
  }

  const defaultSettings = Object.entries(selectedDefaultSettings || {})
    .filter(([, selection]) =>
      Array.isArray(selection) ? selection.length > 0 : selection?.key,
    )
    .map(([fieldName, selection]) => ({
      name: fieldName,
      data: Array.isArray(selection)
        ? selection.map((s) => ({ key: s.key, value: s.value }))
        : [{ key: selection.key, value: selection.value }],
      isArray: Array.isArray(selection),
    }));

  const finalDefaultSettings = isUpdateMode
    ? mergeDefaultSettings(setting?.defaultSettings || [], defaultSettings)
    : defaultSettings;

  return {
    integrationIds,
    defaultSettings: finalDefaultSettings,
    uuid: settingId || setting?.uuid,
  };
};

export const normalizeDefaultSettings = (defaultSettings) => {
  if (!Array.isArray(defaultSettings) || !defaultSettings.length) return {};
  return defaultSettings.reduce((acc, item) => {
    if (!item?.name || !Array.isArray(item.data)) return acc;
    acc[item.name] = item.data.length > 1 ? item.data : item.data[0] || {};
    return acc;
  }, {});
};

export const THIRD_STEP_FETCHERS = {
  [PLATFORMS.JIRA?.toLowerCase()]: async ({
    settingUuid,
    workspaceId,
    project,
  }) => {
    return fetchJiraBoards(settingUuid, workspaceId, project.key);
  },

  [PLATFORMS.CLICKUP?.toLowerCase()]: async ({ settingUuid, projectId }) => {
    return fetchClickupLists(settingUuid, projectId);
  },
};

export const isThreeStepPlatform = (platformName) => {
  const config = PLATFORM_OPTIONS[platformName?.toLowerCase()];

  return config?.preSteps?.length >= 3;
};

export const loadThirdStepItems = async ({
  platformName,
  settingUuid,
  workspaceId,
  projectId,
  projects,
  fetchJiraBoards,
  fetchClickupLists,
}) => {
  const fetcher = THIRD_STEP_FETCHERS[platformName?.toLowerCase()];

  if (!fetcher) return [];

  const selectedProject = projects.find(
    (project) => String(project.id) === String(projectId),
  );

  const response = await fetcher({
    settingUuid,
    workspaceId,
    projectId,
    project: selectedProject,
    fetchJiraBoards,
    fetchClickupLists,
  });

  return response?.data || [];
};
